<template>
  <div>
    <HttpContentView :endpoint="utilities.path('communities/@/servers/all', communityId)" :params="{ serverId: selectedServerId, playerId: playerId }">
      <template v-slot:content="{ data }">
        <div class="d-flex align-items-center flex-wrap g-2 mb-3" v-if="data.length > 1">
          <div v-for="server in data" :key="server.id">
            <ButtonView type="normal" color="secondary" size="small" :active="selectedServerId === server.id" @click="selectedServerId === server.id ? selectedServerId = undefined : selectedServerId = server.id">
              <div class="d-flex align-items-center g-2">
                <i class="fa-duotone fa-server" :style="{ color: server.color }"></i>
                <span>{{ server.name }}</span>
              </div>
            </ButtonView>
          </div>
        </div>
      </template>
    </HttpContentView>
    <HistoricDateRangePickerView v-model:from="from" v-model:to="to" :min="minimumDate" :max="maximumDate" class="my-4"/>
    <HttpContentView :endpoint="utilities.path('communities/@/modules/analytics/statistics', communityId)" :params="params">
      <template v-slot:content="{ data }">
        <div class="d-flex flex-column g-4">
          <div class="card">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between">
                <h3 class="d-flex align-items-center g-2">
                  <i class="fas fa-user text-info"></i>
                  <span>{{ translate('uniquePlayers') }}</span>
                </h3>
                <span class="fw-bold">{{ data.totals.uniquePlayers }}</span>
              </div>
              <LineChart v-if="data.sessions.length > 0" :data="data.sessions.map(item => item.uniquePlayers)" :labels="data.sessions.map(item => utilities.formatDate(item.date))" :label="translate('uniquePlayers')" color="#11cdef"/>
            </div>
          </div>
          <div class="card">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between">
                <h3 class="d-flex align-items-center g-2">
                  <i class="fas fa-calendar-week text-warning"></i>
                  <span>{{ translate('sessions') }}</span>
                </h3>
                <span class="fw-bold">{{ data.totals.sessions }}</span>
              </div>
              <LineChart v-if="data.sessions.length > 0" :data="data.sessions.map(item => item.sessions)" :labels="data.sessions.map(item => utilities.formatDate(item.date))" :label="translate('sessions')" color="#fb6340"/>
            </div>
          </div>
          <div class="card">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between">
                <h3 class="d-flex align-items-center g-2">
                  <i class="fas fa-hourglass text-primary"></i>
                  <span>{{ translate('sessions.averageDuration') }}</span>
                </h3>
                <span class="fw-bold">{{ utilities.formatDuration(data.totals.averageSessionDuration, true) }}</span>
              </div>
              <LineChart v-if="data.sessions.length > 0" :data="data.sessions.map(item => Math.round(item.averageSessionDuration / 60 * 100) / 100)" :labels="data.sessions.map(item => utilities.formatDate(item.date))" :label="translate('sessions.averageDuration')" color="#f18616" :format-value="value => value + ' minutes'"/>
            </div>
          </div>
        </div>
      </template>
    </HttpContentView>
  </div>
</template>

<script setup>
import {ButtonView, HttpContentView, utilities} from "@/niftybit";
import {DateTime} from "luxon";
import LineChart from "@/components/other/charts/LineChart.vue";
import HistoricDateRangePickerView from "@/systems/modules/analytics/HistoricDateRangePickerView.vue";
import {computed, ref} from "vue";

const props = defineProps({
  communityId: String,
  playerId: String
});

const selectedServerId = ref(undefined);
const from = ref(DateTime.utc().toLocal().minus({ days: 30 }).startOf("day").toISO());
const to = ref(DateTime.utc().toLocal().minus({ days: 1 }).endOf("day").toISO());

const minimumDate = DateTime.utc().minus({ days: 90 }).toISO();
const maximumDate = DateTime.utc().toLocal().toISO();

const params = computed(() => ({
  playerId: props.playerId,
  serverId: selectedServerId.value,
  timezone: DateTime.utc().toLocal().zone.name,
  from: DateTime.fromISO(from.value).startOf("day").toISO(),
  to: DateTime.fromISO(to.value).endOf("day").toISO()
}));

</script>